import Icon from "components/AppIcon";
import { SIDEBAR_NAV, SIDEBAR_SETTINGS_LINK } from "config/sidebarNavConfig";
import React from "react";
import { Link, useLocation } from "react-router-dom";

function findActiveGroup(pathname) {
  return SIDEBAR_NAV.find((group) =>
    group.children.some((child) => {
      const childPath = child.path.split("#")[0];
      return pathname === childPath || pathname.startsWith(`${childPath}/`);
    })
  ) ?? null;
}

function findActiveChild(pathname, hash = "") {
  for (const group of SIDEBAR_NAV) {
    for (const child of group.children) {
      const [childPath, childHash = ""] = child.path.split("#");
      if (pathname !== childPath) continue;
      if (childHash && hash !== `#${childHash}`) continue;
      if (child.end && hash) continue;
      return child;
    }
  }
  return null;
}

function Header({
  variant = "default",
  className = "",
  mobileNavOpen = false,
  onMobileNavToggle,
  ...props
}) {
  const location = useLocation();
  const activeGroup = findActiveGroup(location.pathname);
  const activeChild = findActiveChild(location.pathname, location.hash);
  const isSettingsPage =
    location.pathname === SIDEBAR_SETTINGS_LINK.path
    || location.pathname.startsWith(`${SIDEBAR_SETTINGS_LINK.path}/`);

  const isCompact = variant === "compact";

  return (
    <header
      className={`
        bg-white border-b border-gray-200 relative
        ${isCompact ? "py-2" : "h-16 pt-2"}
        ${className}
      `}
      {...props}
    >
      <div className="max-w-full mx-auto px-4 sm:px-2 lg:px-2">
        <div className="flex justify-between items-center">
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:p-2 focus:bg-primary-600 focus:text-white focus:z-50"
          >
            Skip to main content
          </a>

          <div className="flex items-center min-w-0">
            <Link to="/analytics/overview" className="flex items-center flex-shrink-0">
              <div className="md:hidden text-primary-600 mr-2">
                <div className="text-primary-600 w-8 h-8 bg-primary-600">
                  <img
                    src="/tmpj5uq7cal.webp"
                    alt="Logo"
                    className="w-full h-full"
                  />
                </div>
              </div>
            </Link>

            {(isSettingsPage || activeGroup) && (
              <nav className="md:flex justify-center ml-2 md:ml-4 min-w-0">
                <div className="px-3 py-2 rounded-md text-sm md:text-lg font-medium flex items-center text-primary-700 bg-primary-50 truncate">
                  <Icon
                    name={isSettingsPage ? SIDEBAR_SETTINGS_LINK.icon : activeGroup.icon}
                    size={16}
                    className="mr-1.5 flex-shrink-0"
                  />
                  <span className="truncate">
                    {isSettingsPage
                      ? SIDEBAR_SETTINGS_LINK.name
                      : (activeChild?.name ?? activeGroup.name)}
                  </span>
                </div>
              </nav>
            )}
          </div>

          <div className="flex items-center space-x-3 ml-auto flex-shrink-0">
            <div className="relative hidden md:block">
              <Link
                to="/login"
                className="px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                onClick={() => {
                  localStorage.removeItem("authToken");
                  localStorage.removeItem("isAdmin");
                }}
              >
                <Icon name="LogOut" size={18} className="mr-2" />
                Log out
              </Link>
            </div>

            <div className="md:hidden">
              <button
                type="button"
                className="p-2 text-gray-500 rounded-md hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={onMobileNavToggle}
                aria-expanded={mobileNavOpen}
                aria-label={mobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
              >
                <Icon name={mobileNavOpen ? "X" : "Menu"} size={24} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
